import type {ChatMessage} from "~/pages/chat-agent/types/chatTypes";

/**
 * A rendered unit of the transcript. Each agent turn opens with a head block; each run of
 * consecutive tool calls collapses into one tools block; `isActive` marks blocks of the turn that
 * is still streaming. `followedByText` marks a tools block after which the turn already sent text.
 * `handoffs` holds the keys (see toolCallKey) of its calls that handed the turn to another agent.
 */
export type ChatBlock =
    | {kind: "head"; key: string; question?: ChatMessage; replies: ChatMessage[]; isActive: boolean}
    | {kind: "message"; message: ChatMessage}
    | {kind: "thought"; message: ChatMessage; isActive: boolean}
    | {kind: "tools"; key: string; calls: ChatMessage[]; isActive: boolean; followedByText: boolean; handoffs: Set<string>};

type HeadBlock = Extract<ChatBlock, {kind: "head"}>;
type ToolsBlock = Extract<ChatBlock, {kind: "tools"}>;

export function isFunctionMessage(message: ChatMessage): boolean {
    return message.event_type === "function_call" || message.event_type === "function_response";
}

/** Identifies a tool call within its section: parallel calls share their event id, the call id tells them apart. */
export function toolCallKey(call: ChatMessage): string {
    return call.callId ?? call.id;
}

/**
 * An agent called as a tool (e.g. Atlas calling `dashgen`) works under that call: its own events stream
 * while the call stays unanswered until it is done. A call named after `author`, from another agent,
 * is marked as such a handoff once `author` replies after it in the turn.
 */
function markHandoffs(turnTools: ToolsBlock[], author: string | undefined) {
    if (!author) return;
    for (const tools of turnTools) {
        for (const call of tools.calls) {
            if (call.content === author && call.author !== author) tools.handoffs.add(toolCallKey(call));
        }
    }
}

/** How a finished agent turn ended — only what the frontend can tell on its own. */
export type TurnOutcome = "complete" | "clarify" | "failed" | "stopped";

export function turnOutcome(replies: ChatMessage[]): TurnOutcome {
    const last = replies[replies.length - 1];
    if (last?.event_type === "stopped") return "stopped";
    if (last?.isError) return "failed";
    if (last?.event_type === "request_input") return "clarify";
    // No final response at the end (or no reply at all): the run was cut off without an error event,
    // e.g. the server crashed, so after a reload the turn still reads as failed.
    return last?.endsTurn ? "complete" : "failed";
}

export function turnUsedTools(replies: ChatMessage[]): boolean {
    return replies.some(isFunctionMessage);
}

/**
 * Drops the tool-call sections of finished turns that ended on an answer (or a question back), when
 * text of that answer follows them; the answer stands on its own. Live turns, failed or stopped ones,
 * and tools with no text after them (a tool result shown as the answer) keep their tools.
 */
export function withoutAnsweredTools(blocks: ChatBlock[]): ChatBlock[] {
    let answered = false;
    return blocks.filter(block => {
        if (block.kind === "head") {
            const outcome = turnOutcome(block.replies);
            answered = !block.isActive && (outcome === "complete" || outcome === "clarify");
        }
        return !(block.kind === "tools" && answered && block.followedByText);
    });
}

/** Every agent that contributed to a turn, in the order they first appeared. */
export function turnAuthors(replies: ChatMessage[]): string[] {
    const authors = replies.flatMap(r => r.type === "bot" && r.author ? [r.author] : []);
    return [...new Set(authors)];
}

/** Groups the transcript for rendering; with `isStreaming`, blocks after the last user message are live. */
export function buildChatBlocks(messages: ChatMessage[], isStreaming: boolean): ChatBlock[] {
    const liveFrom = isStreaming
        ? messages.findLastIndex(m => m.type === "user") + 1
        : messages.length;
    const blocks: ChatBlock[] = [];
    let question: ChatMessage | undefined;
    let head: HeadBlock | undefined;
    let turnTools: ToolsBlock[] = [];

    messages.forEach((message, i) => {
        const isActive = i >= liveFrom;
        if (message.type === "user") {
            // The previous question got no reply at all: its turn still gets a (failed) head.
            if (question && !head) {
                blocks.push({kind: "head", key: `head-${i}`, question, replies: [], isActive: false});
            }
            question = message;
            head = undefined;
            turnTools = [];
            blocks.push({kind: "message", message});
            return;
        }
        if (!head) {
            head = {kind: "head", key: `head-${i}`, question, replies: [], isActive};
            blocks.push(head);
        }
        head.replies.push(message);
        markHandoffs(turnTools, message.author);

        if (message.event_type === "stopped") {
            // Ends the turn (see turnOutcome) but has nothing to show.
            return;
        }
        if (isFunctionMessage(message)) {
            const last = blocks[blocks.length - 1];
            if (last?.kind === "tools") {
                last.calls.push(message);
            } else {
                const tools: ToolsBlock = {
                    kind: "tools",
                    key: message.id,
                    calls: [message],
                    isActive,
                    followedByText: false,
                    handoffs: new Set(),
                };
                turnTools.push(tools);
                blocks.push(tools);
            }
        } else if (message.event_type === "thought") {
            blocks.push({kind: "thought", message, isActive});
        } else {
            // Text (even its first partial chunk) follows every tools block of the turn so far.
            turnTools.forEach(tools => tools.followedByText = true);
            blocks.push({kind: "message", message});
        }
    });

    // The last question has no reply yet: while streaming it was just sent, so the working head shows
    // before the first reply arrives; otherwise its run ended without one.
    if (!head && (question || isStreaming)) {
        blocks.push({kind: "head", key: `head-${messages.length}`, question, replies: [], isActive: isStreaming});
    }

    return blocks;
}
